import { formatDate } from "./format"
import { getLocale, localized, t } from "./i18n"

/**
 * The delivery booking system (book.highclub.store, repo High-Club-Repo) inside HQ. Its data stays
 * in the booking app; HQ reads and changes it through /hq-booking/* on its own Worker, which admits
 * signed-in owners only and adds the booking app's secret (worker/index.ts bookingProxy). The rules
 * are the booking app's (src/services/hq-admin.ts there).
 */

export type BookingKind = "MEETUP" | "HOME"
export type BookingStatus = "CONFIRMED" | "MOVED" | "NEEDS_ATTENTION" | "CANCELLED" | "COMPLETED"

export type BookingSlim = {
  id: string
  ref: string
  kind: BookingKind
  status: BookingStatus
  name: string
  instagram: string
  phone: string
  email: string
  orderNumber: string | null
  /** A site order id ("order_…"), "custom:<number>", or null. */
  orderId: string | null
  orderSource: string | null
  outsideArea: boolean
  address: string | null
  floor: string | null
  geocodeStatus: "OK" | "APPROXIMATE" | "FAILED" | null
  routePosition: number | null
  assignedDate: string | null
  createdAt: number
}

export type BookingOverview = {
  today: string
  nextWeekStart: string
  nextWeekPublished: boolean
  nextWeekSource: "MANUAL" | "ROLLED_OVER" | null
  needsAttention: BookingSlim[]
  flagged: BookingSlim[]
  meetupDays: Array<{ date: string; booked: number; slots: Array<{ id: string; time: string; capacity: number; status: "OPEN" | "CLOSED"; bookings: BookingSlim[] }> }>
  homeDays: Array<{ date: string; windowStart: string; windowEnd: string; status: string; verdict: string | null; planned: boolean; minStops: number; maxStops: number; stops: BookingSlim[] }>
  recent: BookingSlim[]
}

export type BookingEvent = { id: string; type: string; detail: string | null; actor: string; at: number }

export type BookingDetail = {
  booking: BookingSlim & {
    addressLine: string | null
    addressArea: string | null
    postalCode: string | null
    addressNotes: string | null
    availableFrom: string | null
    availableUntil: string | null
    availabilityNotes: string | null
    preferredDate1: string | null
    preferredDate2: string | null
    feeCents: number
    mapUrl: string | null
    updatedAt: number
  }
  slot: { id: string; date: string; time: string; status: string } | null
  day: { date: string; windowStart: string; windowEnd: string; status: string } | null
  meetingPoint: { label: string; mapUrl: string }
  meetupOptions: Array<{ id: string; date: string; time: string; remaining: number }>
  homeOptions: Array<{ date: string; windowStart: string; windowEnd: string; remaining: number }>
  events: BookingEvent[]
}

export type DayStop = {
  bookingId: string
  position: number
  arrival: string
  departure: string
  legKm: number
  waitMinutes: number
  lateByMinutes: number
  booking: BookingSlim
  addressNotes: string | null
  availableFrom: string | null
  availableUntil: string | null
  availabilityNotes: string | null
  lat: number | null
  lng: number | null
  mapUrl: string | null
}

export type DayView = {
  date: string
  base: { label: string; lat: number; lng: number; mapUrl: string }
  home: {
    date: string
    windowStart: string
    windowEnd: string
    status: string
    minStops: number
    maxStops: number
    locked: boolean
    /** Made from HQ for an arranged booking: runs, and is never offered to customers. */
    arranged?: boolean
    plan: { verdict: string; totalKm: number; driveMinutes: number; finish: string; estimated: boolean; computedAt: number } | null
    stops: DayStop[]
    unrouted: Array<{ booking: BookingSlim; mapUrl: string | null; reason: "address_not_found" | "not_routed_yet" }>
    routeUrl: string | null
  } | null
  meetups: Array<{ id: string; time: string; status: string; capacity: number; bookings: BookingSlim[] }>
}

export type ScheduleView = {
  monday: string
  dates: string[]
  today: string
  published: boolean
  publishedAt: number | null
  source: "MANUAL" | "ROLLED_OVER" | null
  slotTimes: string[]
  defaultWindow: { start: string; end: string }
  slots: Array<{ date: string; time: string; status: "OPEN" | "CLOSED"; locked: boolean }>
  homeDays: Array<{ date: string; windowStart: string; windowEnd: string; status: string; stops: number; locked: boolean }>
}

export type AreasView = {
  rules: Array<{ id: string; code: string; name: string; active: boolean; notes: string | null; deliveries: number }>
  demand: Array<{ postalCode: string; count: number; bookings: number; name: string | null; lastAt: number }>
}

export type SchedulingConfig = {
  meetupCutoffDaysBefore: number
  meetupSlotMinutes: number
  meetupSlotCapacity: number
  homeCutoffHoursBeforeWindow: number
  homeMinStopsPerDay: number
  homeMaxStopsPerDay: number
  homeMaxKmFromBase: number
  homeMaxKmBetweenStops: number
  homeStopServiceMinutes: number
  homeDeliveryFeeCents: number
  base: { label: string; publicLabel: string; mapUrl: string; lat: number; lng: number }
  defaultHomeWindow: { start: string; end: string }
}

export type BookingSettingsView = {
  config: SchedulingConfig
  defaults: SchedulingConfig
  integrations: { googleConfigured: boolean; emailConfigured: boolean; mailFrom: string; adminAlerts: string[]; pending: number; sent: number; failed: number }
}

export type OrderBooking = { id: string; ref: string; kind: BookingKind; status: BookingStatus; date: string | null; time: string | null; window: string | null }
export type GeocodeCheck = { status: "OK" | "APPROXIMATE" | "FAILED"; formatted: string | null; postalCode: string | null; lat: number | null; lng: number | null; mapUrl: string | null }

export type BookOnBehalfBody = {
  kind: BookingKind
  source: "site" | "custom" | "hq"
  orderId?: string
  orderNumber?: string
  firstName: string
  lastName: string
  instagram: string
  phone: string
  email: string
  meetup?: { date: string; time: string }
  home?: {
    date: string
    windowStart?: string
    windowEnd?: string
    addressLine: string
    addressArea: string
    postalCode: string
    addressFloor?: string
    addressNotes?: string
    availableFrom?: string
    availableUntil?: string
    availabilityNotes?: string
  }
  notify: boolean
}

async function call<T>(path: string, init?: { method?: "POST"; body?: unknown }): Promise<T> {
  const response = await fetch(`/hq-booking/${path}`, {
    method: init?.method ?? "GET",
    credentials: "same-origin",
    headers: init?.method ? { "content-type": "application/json" } : undefined,
    body: init?.method ? JSON.stringify(init.body ?? {}) : undefined,
  })
  const body = (await response.json().catch(() => ({}))) as { message?: string }
  // The booking app's own message is English prose: errorMessage() puts it through ts() when it is shown.
  if (!response.ok) throw new Error(body.message || t("The booking system answered {status}", { status: response.status }))
  return body as T
}

export const bookingApi = {
  overview: () => call<BookingOverview>("overview"),
  booking: (id: string) => call<BookingDetail>(`bookings/${encodeURIComponent(id)}`),
  day: (date: string) => call<DayView>(`days/${date}`),
  schedule: (week?: string) => call<ScheduleView>(`schedule${week ? `?week=${week}` : ""}`),
  areas: () => call<AreasView>("areas"),
  settings: () => call<BookingSettingsView>("settings"),
  orders: (ids: string[]) => call<{ bookings: Record<string, OrderBooking>; links: Record<string, string> }>(`orders?ids=${ids.map(encodeURIComponent).join(",")}`),
  book: (body: BookOnBehalfBody) => call<{ ok: true; id: string; ref: string; geocode: GeocodeCheck | null }>("bookings", { method: "POST", body }),
  move: (id: string, body: { slotId?: string; date?: string; time?: string; windowStart?: string; windowEnd?: string; notify: boolean }) =>
    call<{ ok: true; ref: string }>(`bookings/${encodeURIComponent(id)}/move`, { method: "POST", body }),
  cancel: (id: string, body: { reason?: string; notify: boolean }) => call<{ ok: true }>(`bookings/${encodeURIComponent(id)}/cancel`, { method: "POST", body }),
  publish: (body: { monday: string; slots: string[]; homeDays: Array<{ date: string; windowStart: string; windowEnd: string }> }) =>
    call<{ ok: true; slots: number; homeDays: number; removed: number }>("schedule", { method: "POST", body }),
  area: (body: { intent: "add" | "bulk" | "exclude" | "toggle" | "delete" | "dismiss-demand"; id?: string; postalCode?: string; name?: string; notes?: string; lines?: string; replace?: boolean; active?: boolean }) =>
    call<{ ok: true; message: string }>("areas", { method: "POST", body }),
  saveSettings: (config: SchedulingConfig) => call<{ ok: true; message: string }>("settings", { method: "POST", body: { intent: "save", config } }),
  settingsAction: (intent: "reset" | "test-google" | "test-email") => call<{ ok: true; message: string }>("settings", { method: "POST", body: { intent } }),
  geocode: (address: { addressLine: string; addressArea: string; postalCode: string }) => call<GeocodeCheck>("geocode", { method: "POST", body: address }),
}

// ── Labels and small rules ──────────────────────────────────────────────────────────────────────

export const KIND_LABEL: Record<BookingKind, string> = localized({ MEETUP: "Meet-up", HOME: "Home delivery" }, "delivery")
export const STATUS_LABEL: Record<BookingStatus, string> = localized(
  {
    CONFIRMED: "Booked",
    MOVED: "Moved to 2nd day",
    NEEDS_ATTENTION: "Needs a call",
    CANCELLED: "Cancelled",
    COMPLETED: "Done",
  },
  "booking"
)
export const STATUS_COLOUR: Record<BookingStatus, "green" | "blue" | "orange" | "red" | "grey"> = {
  CONFIRMED: "green",
  MOVED: "blue",
  NEEDS_ATTENTION: "orange",
  CANCELLED: "red",
  COMPLETED: "grey",
}

/** The day planner's verdicts (booking app src/domain/day-plan.ts), in words. */
export function verdictLabel(verdict: string | null): string {
  switch (verdict) {
    case null:
    case undefined:
      return t("No route yet")
    case "OK":
      return t("Runs")
    case "TOO_FEW_STOPS":
      return t("Too few stops so far")
    case "TOO_SCATTERED":
      return t("Stops too far apart for one route")
    default:
      return verdict.replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase())
  }
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** "2026-09-29" → "Tue 29 Sep" / «Τρί 29 Σεπ» (a calendar date: no time zone involved). */
export function dayLabel(date: string | null | undefined): string {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return date ?? ""
  const [y, m, d] = date.split("-").map(Number)
  // Greek goes through the shared date format; noon UTC is the same calendar day in Athens.
  if (getLocale() === "el") return formatDate(new Date(Date.UTC(y, m - 1, d, 12)), "weekday")
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay()
  return `${WEEKDAYS[weekday]} ${d} ${MONTHS[m - 1]}`
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number)
  const next = new Date(Date.UTC(y, m - 1, d + days))
  return next.toISOString().slice(0, 10)
}

/** When a booking happens, in words: "Tue 29 Sep · 18:00" or "Tue 29 Sep · 17:00–21:00". */
export function whenLabel(b: { date: string | null; time: string | null; window: string | null }): string {
  return [dayLabel(b.date), b.time ?? b.window].filter(Boolean).join(" · ")
}

/** "Maria Papadopoulou" → first and last name, for the booking form. */
export function splitName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean)
  if (parts.length <= 1) return { firstName: parts[0] ?? "", lastName: "" }
  return { firstName: parts.slice(0, -1).join(" "), lastName: parts[parts.length - 1] }
}

/** Where a booking's order lives in HQ. */
export function orderLink(b: Pick<BookingSlim, "orderId">): string | null {
  if (!b.orderId) return null
  if (b.orderId.startsWith("order_")) return `/orders/${b.orderId}`
  const custom = /^custom:(\d+)$/.exec(b.orderId)
  return custom ? `/custom/${custom[1]}` : null
}

export const bookingKeyOfCustom = (number: string) => `custom:${number.replace(/\D/g, "")}`

/** Minutes → "1 h 25 min" / «1 ώρα 25 λεπτά». */
export function durationLabel(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = Math.round(minutes % 60)
  return [h ? t.n(h, "{n} h", "{n} h") : "", t.n(m, "{n} min", "{n} min")].filter(Boolean).join(" ")
}

/** "Is this address good enough to send a courier to?" in words, from Google's answer. */
export function geocodeWords(status: GeocodeCheck["status"] | null | undefined): { label: string; colour: "green" | "orange" | "red" | "grey" } {
  if (status === "OK") return { label: t("Found exactly"), colour: "green" }
  if (status === "APPROXIMATE") return { label: t("Only the area found"), colour: "orange" }
  if (status === "FAILED") return { label: t("Not found on the map"), colour: "red" }
  return { label: t("Not checked"), colour: "grey" }
}

/**
 * The BOX NOW portal's address line ("Street & Number, City, Postcode", backend
 * lib/hq/boxnow-contact.ts) back into the booking form's three fields. Never invents: a part that is
 * not there stays empty.
 */
export function addressParts(line: string | null | undefined): { addressLine: string; addressArea: string; postalCode: string } {
  const parts = (line ?? "").split(",").map((p) => p.trim()).filter(Boolean)
  let postalCode = ""
  const last = parts[parts.length - 1] ?? ""
  if (/^\d{3}\s?\d{2}$/.test(last)) {
    postalCode = last.replace(/\s/g, "")
    parts.pop()
  }
  return { addressLine: parts[0] ?? "", addressArea: parts.slice(1).join(", "), postalCode }
}

/** Which delivery area rule covers a postcode: its own 5-digit rule, else its 4, else its 3-digit prefix (the booking app's rule). */
export function coveringRule<T extends { code: string }>(rules: T[], postcode: string): T | null {
  const code = postcode.replace(/\s/g, "")
  if (!/^\d{5}$/.test(code)) return null
  for (const key of [code, code.slice(0, 4), code.slice(0, 3)]) {
    const found = rules.find((r) => r.code === key)
    if (found) return found
  }
  return null
}
